import { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Mail, Store, Handshake, MapPin, Phone, ArrowUpRight, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { MaskedLines, FadeIn, Reveal } from "@/components/Motion";

const PORTAL_URL = process.env.REACT_APP_PORTAL_URL;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  { value: "general", label: "General question" },
  { value: "business", label: "Business advertising" },
  { value: "partnership", label: "Partner program" },
];

const SECTIONS = [
  {
    id: "businesses",
    icon: Store,
    overline: "For businesses & advertisers",
    title: "Put your business on the route riders actually ride.",
    body: "VeloPaths shows cyclists a curated map of approved local stops — never the noise of every pin on the internet. As an advertiser, you get a verified listing with your category pin, hours, photos, and rider deals, plus featured placement and seasonal promotions. It's all self-serve: create your listing, pick a monthly plan, and go live in the Advertiser Portal.",
    cta: "Advertiser Portal",
    testid: "btn-advertiser-portal",
  },
  {
    id: "rental-shops",
    icon: Handshake,
    overline: "For partners",
    title: "Turn your local knowledge into a branded map that earns.",
    body: "Partners receive a branded presence within Velopaths and the tools to manage routes, bike paths, stops, local business recommendations, and advertiser relationships within their territory. Whether you're a bike shop, cycling club, trail advocate, nonprofit, tourism organization, or local cycling expert, Velopaths helps you create better cycling experiences while participating in shared advertising revenue opportunities.",
    cta: "Partner Portal",
    testid: "btn-rental-shop-portal",
  },
];

const INFO = [
  { icon: Mail, title: "General questions", value: "info@velopaths.com", href: "mailto:info@velopaths.com" },
  { icon: Handshake, title: "Advertiser & partner support", value: "info@velopaths.com", href: "mailto:info@velopaths.com" },
  {
    icon: MapPin,
    title: "Velopaths, LLC",
    lines: ["270 Bellevue Avenue", "PMB #1097", "Newport, RI 02840", "United States"],
  },
  { icon: Phone, title: "Call us", value: "(401) 646-0066" },
];

export default function Contact() {
  const location = useLocation();
  const initialTopic = TOPICS.some((t) => t.value === location.state?.tab)
    ? location.state.tab
    : "general";
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    topic: initialTopic,
    message: "",
    website: "",
  });
  const [sending, setSending] = useState(false);
  const startedAt = useRef(Date.now());

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, elapsed_ms: Date.now() - startedAt.current });
      toast.success("Message sent!", {
        description: "Thanks for reaching out — we'll reply to your email shortly.",
      });
      setForm({ name: "", email: "", organization: "", topic: "general", message: "", website: "" });
    } catch {
      toast.error("Couldn't send your message", {
        description: "Please try again in a moment, or email info@velopaths.com directly.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="page-contact">
      <section className="relative overflow-hidden bg-[#0B2B22] pt-40 pb-20 sm:pb-24">
        <div className="grain absolute inset-0" />
        <div className="absolute -right-24 -top-24 size-72 rounded-full bg-[#2D6A4F]/40 blur-3xl" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
          <FadeIn delay={0.05}>
            <p className="font-mono text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-[#7FB998]">
              Contact
            </p>
          </FadeIn>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#F4F7F4]">
            <MaskedLines lines={["Let's Build the", "Path Together."]} />
          </h1>
          <FadeIn delay={0.55}>
            <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#F4F7F4]/75">
              Advertising and partnerships are now self-serve — pick your lane below and head
              straight to your portal.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 sm:px-10 sm:py-28 lg:px-16">
        <div className="grid gap-8 lg:grid-cols-2">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.12} className="h-full">
              <div
                data-testid={`portal-section-${s.id}`}
                className="flex h-full flex-col rounded-[2rem] border border-[#154234]/10 bg-white p-8 shadow-xl sm:p-10"
              >
                <span className="grid size-12 place-items-center rounded-2xl bg-[#0B2B22] text-[#7FB998]">
                  <s.icon size={21} />
                </span>
                <p className="mt-6 font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#2D6A4F]">
                  {s.overline}
                </p>
                <h2 className="mt-3 font-display text-xl sm:text-2xl font-bold tracking-tight text-[#0B2B22]">
                  {s.title}
                </h2>
                <p className="mt-4 flex-1 text-sm sm:text-base leading-relaxed text-[#52635B]">
                  {s.body}
                </p>
                <a
                  href={PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={s.testid}
                  className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#E05A47] px-7 py-3.5 text-sm font-bold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#c94a39] sm:w-auto"
                >
                  {s.cta} <ArrowUpRight size={15} />
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid gap-10 border-t border-[#154234]/10 pt-16 lg:grid-cols-2 lg:gap-14">
          <Reveal>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#2D6A4F]">
              Talk to a human
            </p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2B22]">
              Send us a message.
            </h2>
            <p className="mt-4 max-w-md text-sm sm:text-base leading-relaxed text-[#52635B]">
              Questions about the app, the map, a trail you'd like to see, or anything else that
              doesn't fit a portal? Write to us here — every message lands directly in
              info@velopaths.com and we reply personally.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="rounded-[2rem] border border-[#154234]/10 bg-white p-8 shadow-xl sm:p-10"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  data-testid="input-contact-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  aria-label="Your name"
                  className="rounded-2xl border border-[#154234]/15 bg-[#F4F7F4] px-5 py-3.5 text-sm text-[#0B2B22] placeholder:text-[#52635B]/60 focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/40"
                />
                <input
                  data-testid="input-contact-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  placeholder="Your email"
                  aria-label="Your email"
                  className="rounded-2xl border border-[#154234]/15 bg-[#F4F7F4] px-5 py-3.5 text-sm text-[#0B2B22] placeholder:text-[#52635B]/60 focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/40"
                />
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <input
                  data-testid="input-contact-organization"
                  type="text"
                  value={form.organization}
                  onChange={update("organization")}
                  placeholder="Business or organization (optional)"
                  aria-label="Business or organization (optional)"
                  className="rounded-2xl border border-[#154234]/15 bg-[#F4F7F4] px-5 py-3.5 text-sm text-[#0B2B22] placeholder:text-[#52635B]/60 focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/40"
                />
                <select
                  data-testid="select-contact-topic"
                  value={form.topic}
                  onChange={update("topic")}
                  aria-label="Topic"
                  className="rounded-2xl border border-[#154234]/15 bg-[#F4F7F4] px-5 py-3.5 text-sm text-[#0B2B22] focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/40"
                >
                  {TOPICS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <textarea
                data-testid="input-contact-message"
                required
                rows={5}
                value={form.message}
                onChange={update("message")}
                placeholder="Your message"
                aria-label="Your message"
                className="mt-4 w-full rounded-2xl border border-[#154234]/15 bg-[#F4F7F4] px-5 py-3.5 text-sm text-[#0B2B22] placeholder:text-[#52635B]/60 focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/40"
              />
              {/* Honeypot — invisible to humans, bots fill it */}
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={update("website")}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                data-testid="contact-honeypot"
                className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
              />
              <button
                type="submit"
                data-testid="btn-contact-submit"
                disabled={sending}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#E05A47] px-7 py-3.5 text-sm font-bold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#c94a39] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 sm:w-auto"
              >
                <Send size={15} /> {sending ? "Sending…" : "Send message"}
              </button>
            </form>
          </Reveal>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {INFO.map((c, i) => (
            <Reveal key={c.title} delay={0.15 + i * 0.08}>
              {c.href ? (
                <a
                  href={c.href}
                  data-testid={`contact-info-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex h-full items-center gap-4 rounded-2xl border border-[#154234]/10 bg-white p-5 transition-transform duration-300 hover:-translate-y-1"
                >
                  <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-[#0B2B22] text-[#7FB998]">
                    <c.icon size={18} />
                  </span>
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-[0.15em] text-[#52635B]">
                      {c.title}
                    </span>
                    <span className="mt-0.5 block font-display text-base font-bold text-[#0B2B22]">
                      {c.value}
                    </span>
                  </span>
                </a>
              ) : (
                <div
                  data-testid={`contact-info-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex h-full items-center gap-4 rounded-2xl border border-[#154234]/10 bg-white p-5"
                >
                  <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-[#0B2B22] text-[#7FB998]">
                    <c.icon size={18} />
                  </span>
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-[0.15em] text-[#52635B]">
                      {c.title}
                    </span>
                    {c.lines ? (
                      <span className="mt-0.5 block font-display text-sm font-bold leading-snug text-[#0B2B22]">
                        {c.lines.map((line) => (
                          <span key={line} className="block">
                            {line}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="mt-0.5 block font-display text-base font-bold text-[#0B2B22]">
                        {c.value}
                      </span>
                    )}
                  </span>
                </div>
              )}
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
